import React, { useState, useEffect } from 'react';
import {
  UserMinus,
  UserCheck,
  UserX,
  Search,
  Filter,
  AlertTriangle,
  History,
  ShieldAlert,
  Mail,
  RefreshCw,
  MoreVertical,
} from 'lucide-react';
import { salesManagerService } from '../services/sales-manager.service';
import type { TeamMember } from '../types';

export const AgentManagementPanel: React.FC = () => {
  const [agents, setAgents] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Modal states
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showFireModal, setShowFireModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<TeamMember | null>(null);

  // Form states
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [confirmFire, setConfirmFire] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchAgents = async () => {
    try {
      setIsLoading(true);
      const response = await salesManagerService.getTeamRecruits({ 
        status: statusFilter === 'all' ? undefined : statusFilter 
      });
      setAgents(response.recruits);
    } catch (err) {
      console.warn('API fetch failed, falling back to mock agents for testing');
      setAgents([
        {
          id: '1',
          userId: 'u1',
          firstName: 'John',
          lastName: 'Smith',
          email: 'john.smith@example.com',
          phone: '555-0123',
          role: 'AGENT',
          status: 'active',
          salesCount: 45,
          recruitCount: 12,
          currentFunnelStep: 8,
          stepName: 'First Paycheck',
          joinedAt: '2025-01-15T10:00:00Z',
          performance: 'overperforming'
        },
        {
          id: '2',
          userId: 'u2',
          firstName: 'Sarah',
          lastName: 'Johnson',
          email: 'sarah.j@example.com',
          phone: '555-0124',
          role: 'AGENT',
          status: 'inactive',
          salesCount: 12,
          recruitCount: 3,
          currentFunnelStep: 6,
          stepName: 'Ready to Sell',
          joinedAt: '2025-02-20T10:00:00Z',
          performance: 'inactive'
        },
        {
          id: '3',
          userId: 'u3',
          firstName: 'Mike',
          lastName: 'Williams',
          email: 'mike.w@example.com',
          phone: '555-0125',
          role: 'AGENT',
          status: 'active',
          salesCount: 28,
          recruitCount: 5,
          currentFunnelStep: 8,
          stepName: 'First Paycheck',
          joinedAt: '2025-03-01T10:00:00Z',
          performance: 'normal'
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, [statusFilter]);

  const handleDeactivate = async () => {
    if (!selectedAgent || !reason) return;
    try {
      setActionLoading(true);
      await salesManagerService.deactivateAgent({
        agentId: selectedAgent.id,
        reason: reason as any,
        notes
      });
      fetchAgents();
      setShowDeactivateModal(false);
    } catch (err) {
      setError('Failed to deactivate agent');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!selectedAgent) return;
    try {
      setActionLoading(true);
      await salesManagerService.reactivateAgent({
        agentId: selectedAgent.id,
        notes
      });
      fetchAgents();
      setShowReactivateModal(false);
    } catch (err) {
      setError('Failed to reactivate agent');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFire = async () => {
    if (!selectedAgent || !reason || !confirmFire) return;
    try {
      setActionLoading(true);
      await salesManagerService.fireAgent({
        agentId: selectedAgent.id,
        reason
      });
      fetchAgents();
      setShowFireModal(false);
    } catch (err) {
      setError('Failed to terminate agent');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredAgents = agents.filter(a => 
    `${a.firstName} ${a.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Agent Management</h2>
          <p className="text-gray-600">Manage status, deactivations, and terminations for your team</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search agents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Agent</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Sales</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Performance</th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-600 mb-2" />
                  <span className="text-gray-500">Loading agents...</span>
                </td>
              </tr>
            ) : filteredAgents.map((agent) => (
              <tr key={agent.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold">
                      {agent.firstName[0]}{agent.lastName[0]}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{agent.firstName} {agent.lastName}</p>
                      <p className="text-xs text-gray-500">{agent.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                    agent.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {agent.status.toUpperCase()}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                  {agent.salesCount} sales
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    agent.performance === 'overperforming' ? 'bg-indigo-100 text-indigo-700' : 
                    agent.performance === 'inactive' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'
                  }`}>
                    {agent.performance.toUpperCase()}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                  {agent.status === 'active' ? (
                    <button 
                      onClick={() => { setSelectedAgent(agent); setReason(''); setNotes(''); setShowDeactivateModal(true); }}
                      className="p-1.5 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all"
                      title="Deactivate"
                    >
                      <UserMinus className="w-4 h-4" />
                    </button>
                  ) : (
                    <button 
                      onClick={() => { setSelectedAgent(agent); setNotes(''); setShowReactivateModal(true); }}
                      className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
                      title="Reactivate"
                    >
                      <UserCheck className="w-4 h-4" />
                    </button>
                  )}
                  <button 
                    onClick={() => { setSelectedAgent(agent); setReason(''); setConfirmFire(false); setShowFireModal(true); }}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                    title="Fire"
                  >
                    <UserX className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Deactivate Modal */}
      {showDeactivateModal && selectedAgent && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 bg-orange-50 border-b border-orange-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <UserMinus className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Deactivate Agent</h3>
                  <p className="text-sm text-orange-800">Temporarily remove access for {selectedAgent.firstName}</p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Reason for Deactivation</label>
                <select 
                  value={reason} 
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="">Select a reason...</option>
                  <option value="performance">Low Performance</option>
                  <option value="inactivity">Inactivity</option>
                  <option value="compliance">Compliance Issue</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Additional Notes</label>
                <textarea 
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Details for the history record..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none min-h-[100px]"
                />
              </div>
            </div>
            <div className="p-6 bg-gray-50 flex gap-3">
              <button onClick={() => setShowDeactivateModal(false)} className="flex-1 py-2 font-bold text-gray-600 hover:bg-gray-200 rounded-xl transition-colors">Cancel</button>
              <button 
                onClick={handleDeactivate} 
                disabled={!reason || actionLoading}
                className="flex-1 py-2 bg-orange-600 text-white font-bold rounded-xl hover:bg-orange-700 disabled:opacity-50 transition-colors shadow-lg shadow-orange-200"
              >
                {actionLoading ? 'Processing...' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate Modal */}
      {showReactivateModal && selectedAgent && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-6 bg-green-50 border-b border-green-100 text-green-900">
              <h3 className="text-xl font-bold">Reactivate Agent</h3>
              <p className="text-sm">Restore access for {selectedAgent.firstName} {selectedAgent.lastName}</p>
            </div>
            <div className="p-6">
              <label className="block text-sm font-bold text-gray-700 mb-1">Reactivation Notes</label>
              <textarea 
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Why is this agent being reinstated?"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none min-h-[100px]"
              />
            </div>
            <div className="p-6 flex gap-3">
              <button onClick={() => setShowReactivateModal(false)} className="flex-1 py-2 font-bold text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
              <button 
                onClick={handleReactivate} 
                disabled={actionLoading}
                className="flex-1 py-2 bg-green-600 text-white font-bold rounded-xl hover:bg-green-700 shadow-lg shadow-green-100"
              >
                {actionLoading ? 'Reactivating...' : 'Confirm Reactivation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fire Modal (Permanent Termination) */}
      {showFireModal && selectedAgent && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border-2 border-red-100">
            <div className="p-8 bg-red-600 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-md border border-white/30">
                  <ShieldAlert className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-black uppercase tracking-tighter">Terminate Employment</h3>
                  <p className="text-red-100 opacity-90 font-medium">Permanent removal from organization</p>
                </div>
              </div>
              <div className="p-4 bg-red-700/50 rounded-xl border border-red-500/50">
                <p className="text-sm font-bold flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  WARNING: This action is irreversible without HR & CEO approval.
                </p>
              </div>
            </div>
            
            <div className="p-8 space-y-6">
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl border border-gray-100">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center text-red-600 font-black">
                  {selectedAgent.firstName[0]}{selectedAgent.lastName[0]}
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">{selectedAgent.firstName} {selectedAgent.lastName}</p>
                  <p className="text-sm text-gray-500">{selectedAgent.email}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-black text-gray-700 uppercase tracking-widest mb-2">Termination Reason</label>
                  <select 
                    value={reason} 
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-500 outline-none transition-colors"
                  >
                    <option value="">Select mandatory reason...</option>
                    <option value="Compliance Violation">Compliance Violation</option>
                    <option value="Fraud/Misconduct">Fraud / Professional Misconduct</option>
                    <option value="Performance">Chronic Underperformance</option>
                    <option value="Policy Violation">Company Policy Violation</option>
                    <option value="Voluntary">Voluntary Resignation</option>
                  </select>
                </div>

                <div className="p-4 bg-red-50 rounded-xl border-2 border-red-100">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={confirmFire}
                      onChange={(e) => setConfirmFire(e.target.checked)}
                      className="w-5 h-5 rounded border-red-300 text-red-600 focus:ring-red-500"
                    />
                    <span className="text-sm font-bold text-red-900 leading-tight">
                      I confirm that I have followed all legal protocols and this termination is justified.
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="p-8 bg-gray-50 border-t border-gray-100 flex gap-4">
              <button 
                onClick={() => setShowFireModal(false)} 
                className="flex-1 py-3 font-bold text-gray-500 hover:bg-gray-200 rounded-xl transition-all"
              >
                CANCEL
              </button>
              <button 
                onClick={handleFire}
                disabled={!reason || !confirmFire || actionLoading}
                className="flex-1 py-3 bg-red-600 text-white font-black rounded-xl hover:bg-red-700 disabled:opacity-30 disabled:grayscale transition-all shadow-xl shadow-red-200 uppercase tracking-widest"
              >
                {actionLoading ? 'TERMINATING...' : 'PERMANENTLY FIRE'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentManagementPanel;
